import React, {
	createContext,
	useState,
	type ReactNode,
	lazy,
	Suspense,
	useMemo,
	useCallback,
} from 'react';
import type { InvitePeopleModalProps, CloseTrigger } from '../../types';
import { fg } from '@atlaskit/platform-feature-flags';

const AKInvitePeopleModal = lazy(
	() => import(/* webpackChunkName: "async-invite-people-modal" */ './InvitePeopleModal'),
);

const AKModalTransition = lazy(() =>
	import(
		/* webpackChunkName: "async-people-menu-modal-transition-dialog" */ '@atlaskit/modal-dialog'
	).then((module) => ({
		default: module.ModalTransition,
	})),
);

type InvitePeopleModalContextProviderProps = {
	children: ReactNode;
};

type InvitePeopleModelOmitContextProps = Omit<InvitePeopleModalProps, 'onCloseHandler'>;

type InvitePeopleModalContextProps = {
	isOpen: boolean;
	openModal: (
		props: InvitePeopleModelOmitContextProps,
		onClose?: (trigger?: CloseTrigger) => void,
	) => void;
	closeModal: (trigger?: CloseTrigger) => void;
};

export const InvitePeopleModalContext = createContext<InvitePeopleModalContextProps>({
	isOpen: false,
	openModal: () => undefined,
	closeModal: () => undefined,
});

export type InvitePeopleState =
	| {
			isOpen: true;
			props: InvitePeopleModelOmitContextProps;
			onClose?: (trigger?: CloseTrigger) => void;
	  }
	| {
			isOpen: false;
			previousProps?: InvitePeopleModelOmitContextProps;
	  };

export const InvitePeopleModalContextProvider = (
	invitePeopleModalContextProviderProps: InvitePeopleModalContextProviderProps,
) => {
	const { children } = invitePeopleModalContextProviderProps;

	const [invitePeopleState, setInvitePeopleState] = useState<InvitePeopleState>({
		isOpen: false,
	});

	const openModal = (
		invitePeopleProps: InvitePeopleModelOmitContextProps,
		onClose?: (trigger?: CloseTrigger) => void,
	) => {
		setInvitePeopleState({
			isOpen: true,
			props: invitePeopleProps,
			onClose,
		});
	};

	const closeModal = useCallback(
		(trigger?: CloseTrigger) => {
			if (fg('use_previous_state_instead_of_direct_access')) {
				setInvitePeopleState((prevState) => {
					if (!prevState.isOpen) {
						return prevState;
					}
					prevState.onClose?.(trigger);
					return {
						previousProps: prevState.props,
						isOpen: false,
					};
				});
			} else {
				if (!invitePeopleState.isOpen) {
					return;
				}
				invitePeopleState.onClose?.(trigger);
				setInvitePeopleState({
					previousProps: invitePeopleState.props,
					isOpen: false,
				});
			}
		},
		[invitePeopleState],
	);

	const reopenModal = useCallback(() => {
		// Reopen function can only reopen invite people if openModal has been called once with props
		setInvitePeopleState((prevState) =>
			!prevState.isOpen && prevState.previousProps
				? {
						props: prevState.previousProps,
						isOpen: true,
					}
				: prevState,
		);
	}, []);

	const context = useMemo(
		() => ({
			openModal,
			closeModal,
			isOpen: invitePeopleState.isOpen,
		}),
		[closeModal, invitePeopleState.isOpen],
	);

	return (
		<InvitePeopleModalContext.Provider value={context}>
			{children}
			{invitePeopleState.isOpen && (
				<Suspense fallback={null}>
					<AKModalTransition>
						<AKInvitePeopleModal
							onCloseHandler={closeModal}
							experimental_onReopenHandler={reopenModal}
							{...invitePeopleState.props}
						/>
					</AKModalTransition>
				</Suspense>
			)}
		</InvitePeopleModalContext.Provider>
	);
};
